import type { ReactNode } from 'react'
import { GramMark } from '../components/ui/icons/GramMark'
import soft from '../styles/soft.module.css'

/**
 * A buy's toast line: "Buy TICKER", the GRAM mark, the amount (and what
 * follows it). The toast's text is a flex row (pixel-toast .tText), so each
 * words part is cut to its capitals (soft.fig) and the mark, a flex item of
 * its own, sits on the middle of the digits by construction (the operator,
 * 2026-11-03: the gem stood low and out of its box under the old
 * vertical-align and an 11 px clip box).
 */
export function buyToastLine(ticker: string, amount: string, suffix = ''): ReactNode {
  return (
    <>
      <span className={soft.fig}>Buy {ticker}</span>
      <GramMark size={11} mono />
      <span className={soft.fig}>
        {amount}
        {suffix}
      </span>
    </>
  )
}

export function buyToastFailed(ticker: string, amount: string, reason: string): ReactNode {
  return buyToastLine(ticker, amount, ` failed · ${reason}`)
}

export function listBuyToastLabel(ticker: string | undefined, amount: string): string {
  const name = ticker?.trim() || 'token'
  const gram = amount.trim()
  return gram ? `Buy ${name} ${gram}` : `Buy ${name}`
}

/** Sell chips are "100%" and the amount field is "100". One trailing percent. */
export function sellToastLabel(ticker: string | undefined, percent: string): string {
  const name = ticker?.trim() || 'token'
  const n = Number.parseFloat(percent.trim())
  const shown = Number.isFinite(n) ? `${n}` : percent.trim().replace(/%+$/g, '')
  return `Sell ${name} ${shown}%`
}

export function sellToastFailed(
  ticker: string | undefined,
  percent: string,
  reason: string
): string {
  return `${sellToastLabel(ticker, percent)} failed · ${reason}`
}

/** The wallet transaction's action phase as our wallet.confirmation push
    carries it; a missing field is unknown, never zero. */
export type WalletActionPhase = {
  skippedActions: number | null
  noFunds: boolean | null
  resultCode: number | null
}

/**
 * Why an external that EXECUTED produced no trade — from our own data only.
 *
 * Two shapes say "not enough GRAM", both from the wallet transaction's
 * action phase on our wallet.confirmation push:
 *   - with IGNORE_ERRORS (+2), the action the wallet could not pay for is
 *     skipped: skipped_actions > 0 (the 2026-10-27 incident, chain-read:
 *     lt 112808452000001 / 112808455000001, skipped_actions=1, result_code=0);
 *   - without it (the current [swap, fee] buy), the whole action phase fails:
 *     no_funds, result_code 37. Source: docs.ton.org tvm-exit-codes, action
 *     phase — 37 "Not enough GRAMs", 38 "Not enough extra currencies" (not
 *     GRAM, so it is not named as such).
 * Anything else is unknown — never "slippage": that toast used to be painted
 * on every such failure without reading anything. A pool refund code is not
 * in our data yet.
 */
export function executedWithoutTradeReason(phase: WalletActionPhase | null | undefined): string {
  const notEnoughGram =
    phase != null &&
    ((phase.skippedActions ?? 0) > 0 ||
      phase.resultCode === 37 ||
      (phase.noFunds === true && phase.resultCode !== 38))
  return notEnoughGram
    ? 'Not enough GRAM — the swap was not sent'
    : 'Swap did not execute — reason unknown'
}
